import {makeAutoObservable} from 'mobx';
import {ICheckboxesTreeItem} from '../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store';
import {isSet} from '../../../../../models/model-types-methods';
import ExchangeLocationsStore from './exchange-locations-store';
import ZonesTreeConverter from '../utilities/zones-tree-converter';
import WarehousesTreeConverter from '../utilities/warehouses-tree-converter';
import {IExchangeLocationRuleRow} from '../utilities/exchange-locations-table-builder';

/** Modal modes for add/edit operations */
export enum MODAL_MODE {
    ADD = 'add',
    EDIT = 'edit',
    EDIT_FALLBACK = 'edit_fallback',
}

/**
 * Modal store managing user selections in warehouse and zone dropdowns.
 * Stores selected trees, validates selections, and builds locations on apply.
 */
class ExchangeLocationsRuleModalStore {

    private readonly _mode: MODAL_MODE;
    private readonly _editingRow: IExchangeLocationRuleRow | null;

    private _selectedWarehousesTree: ICheckboxesTreeItem[] | undefined = undefined;
    private _selectedZonesTree: ICheckboxesTreeItem[] | undefined = undefined;

    private readonly _exchangeLocationsStore: ExchangeLocationsStore;
    private readonly _zonesTreeConverter: ZonesTreeConverter;
    private readonly _warehousesTreeConverter: WarehousesTreeConverter;

    /**
     * Creates modal store and initializes converters.
     * If editingRow provided, initializes selected trees from its locations.
     */
    constructor(
        mode: MODAL_MODE,
        editingRow: IExchangeLocationRuleRow | null,
        exchangeLocationsStore: ExchangeLocationsStore
    ) {
        makeAutoObservable(this, {}, {autoBind: true});

        this._mode = mode;
        this._editingRow = editingRow;
        this._exchangeLocationsStore = exchangeLocationsStore;

        this._zonesTreeConverter = new ZonesTreeConverter(
            this._exchangeLocationsStore.getCountries()
        );

        const disabledWarehouseIds = this._calculateDisabledWarehouseIds();

        this._warehousesTreeConverter = new WarehousesTreeConverter(
            this._exchangeLocationsStore.getWarehouses(),
            disabledWarehouseIds
        );

        if (editingRow) {
            if (this._mode === MODAL_MODE.EDIT_FALLBACK && editingRow.locations.length === 0) {
                this._selectedWarehousesTree = [];
            }
            else {
                this._selectedWarehousesTree = this._warehousesTreeConverter.buildValueTree(editingRow.locations);
            }

            this._selectedZonesTree = this._zonesTreeConverter.buildValueTree(editingRow.locations);
        }
    }

    /** Returns warehouse options for dropdown */
    public getWarehousesOptions(): ICheckboxesTreeItem[] {
        return this._warehousesTreeConverter.getOptionsTree();
    }

    /** Returns currently selected warehouses */
    public getWarehousesValue(): ICheckboxesTreeItem[] | undefined {
        return this._selectedWarehousesTree;
    }

    /** Returns items to display as labels. In compressed mode with [] returns all options. */
    public getWarehousesLabels(): ICheckboxesTreeItem[] {
        if (!isSet(this._selectedWarehousesTree)) {
            return [];
        }

        if (this.isFallbackMode() && this._selectedWarehousesTree.length === 0) {
            return this.getWarehousesOptions();
        }

        return this._selectedWarehousesTree;
    }

    /** Returns zone options for dropdown */
    public getZonesOptions(): ICheckboxesTreeItem[] {
        return this._zonesTreeConverter.getOptionsTree();
    }

    /** Returns currently selected zones */
    public getZonesValue(): ICheckboxesTreeItem[] | undefined {
        return this._selectedZonesTree;
    }

    /** Returns true if editing fallback rule */
    public isFallbackMode(): boolean {
        return this._mode === MODAL_MODE.EDIT_FALLBACK;
    }

    /**
     * Returns set of warehouse IDs that should be disabled in dropdown.
     * ADD: all used warehouses. EDIT: used warehouses except editing row. EDIT_FALLBACK: none.
     */
    private _calculateDisabledWarehouseIds(): Set<string> {
        const usedWarehouseIds = this._exchangeLocationsStore.getUsedWarehouseIds();

        if (this._mode === MODAL_MODE.EDIT_FALLBACK) {
            return new Set();
        }

        if (this._mode === MODAL_MODE.EDIT && this._editingRow) {
            const editingWarehouseIds = new Set(
                this._editingRow.locations.map((location) => location.location_id)
            );

            const disabledIds = new Set<string>();
            usedWarehouseIds.forEach((warehouseId) => {
                if (!editingWarehouseIds.has(warehouseId)) {
                    disabledIds.add(warehouseId);
                }
            });

            return disabledIds;
        }

        return usedWarehouseIds;
    }

    /**
     * Returns true if Apply button should be enabled.
     * EDIT_FALLBACK: requires 1+ warehouse (compressed: [] is valid). ADD/EDIT: requires 1+ warehouse AND 1+ zone.
     */
    public canApply(): boolean {
        if (this._mode === MODAL_MODE.EDIT_FALLBACK) {
            return isSet(this._selectedWarehousesTree);
        }

        const hasWarehouses = isSet(this._selectedWarehousesTree) && this._selectedWarehousesTree.length > 0;
        const hasZones = isSet(this._selectedZonesTree) && this._selectedZonesTree.length > 0;

        return hasWarehouses && hasZones;
    }

    /** Stores new warehouse selection */
    public onWarehousesChanged(newWarehousesTree: ICheckboxesTreeItem[] | undefined): void {
        this._selectedWarehousesTree = newWarehousesTree;
    }

    /** Stores new zone selection */
    public onZonesChanged(newZonesTree: ICheckboxesTreeItem[] | undefined): void {
        this._selectedZonesTree = newZonesTree;
    }

    /**
     * Builds locations from selected trees using converters and passes to main store.
     * EDIT_FALLBACK: warehouse IDs + REST_OF_WORLD (compressed: [] means all). ADD/EDIT: warehouse IDs × zone selections.
     */
    public apply(): void {
        if (!this.canApply()) {
            return;
        }

        let locations;

        if (this._mode === MODAL_MODE.EDIT_FALLBACK) {
            let warehouseIds;

            if (isSet(this._selectedWarehousesTree) && this._selectedWarehousesTree.length === 0) {
                warehouseIds = this._exchangeLocationsStore.getWarehouses().map((platformLocation) => platformLocation.location_id);
            }
            else {
                warehouseIds = this._warehousesTreeConverter.extractWarehouseIds(this._selectedWarehousesTree);
            }

            locations = this._warehousesTreeConverter.buildFallbackLocations(warehouseIds);
        }
        else {
            const warehouseIds = this._warehousesTreeConverter.extractWarehouseIds(this._selectedWarehousesTree);
            locations = this._zonesTreeConverter.extractLocations(this._selectedZonesTree, warehouseIds);
        }

        if (this._mode === MODAL_MODE.ADD) {
            this._exchangeLocationsStore.upsertRule(null, locations);
        }
        else {
            this._exchangeLocationsStore.upsertRule(this._editingRow, locations);
        }
    }
}

export default ExchangeLocationsRuleModalStore;